import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { QUERY_KEYS } from '@/api/queryKeys';
import { ApiError } from '@/api/client';
import { getTreeOverlay } from '@/api/medicalOverlay.api';
import {
  searchCatalog,
  getConditions,
  createCondition,
  updateCondition,
  deleteCondition,
  getTraits,
  createTrait,
  updateTrait,
  deleteTrait,
  getConsents,
  grantConsent,
  revokeConsent,
  type ConditionCategory,
  type CreateConditionDto,
  type UpdateConditionDto,
  type CreateTraitDto,
  type UpdateTraitDto,
  type MedicalConsent,
} from '@/api/medical.api';

const onError = (fallback: string) => (error: ApiError) =>
  toast.error(error.message || fallback);

// ─── Katalog ─────────────────────────────────────────────────────────────

export function useCatalogSearch(
  params?: { q?: string; category?: ConditionCategory; hereditary?: boolean },
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: QUERY_KEYS.medical.catalog(params),
    queryFn: () => searchCatalog(params),
    staleTime: 5 * 60_000,
    enabled: options?.enabled ?? true,
  });
}

// ─── Stammbaum-Overlay ───────────────────────────────────────────────────

export function useTreeOverlay(catalogId: string | null) {
  return useQuery({
    queryKey: ['medical', 'overlay', catalogId],
    queryFn: () => getTreeOverlay(catalogId as string),
    enabled: !!catalogId,
    staleTime: 60_000,
  });
}

// ─── Diagnosen ───────────────────────────────────────────────────────────

export function useConditions(personId: string) {
  return useQuery({
    queryKey: QUERY_KEYS.medical.conditions(personId),
    queryFn: () => getConditions(personId),
    enabled: !!personId,
    // Länger frisch halten: Die Vererbungs-Seite lädt diese Daten für viele
    // Vorfahren gleichzeitig – ohne staleTime löst jedes Umschalten die
    // komplette Request-Welle (inkl. serverseitigem Audit-Log) erneut aus.
    // Mutationen invalidieren gezielt, Aktualität leidet also nicht.
    staleTime: 60_000,
  });
}

export function useCreateCondition(personId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dto: CreateConditionDto) => createCondition(personId, dto),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QUERY_KEYS.medical.conditions(personId) });
      toast.success('Diagnose gespeichert.');
    },
    onError: onError('Diagnose konnte nicht gespeichert werden.'),
  });
}

export function useUpdateCondition(personId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, dto }: { id: string; dto: UpdateConditionDto }) => updateCondition(id, dto),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QUERY_KEYS.medical.conditions(personId) });
      toast.success('Diagnose aktualisiert.');
    },
    onError: onError('Diagnose konnte nicht aktualisiert werden.'),
  });
}

export function useDeleteCondition(personId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteCondition(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QUERY_KEYS.medical.conditions(personId) });
      toast.success('Diagnose entfernt.');
    },
    onError: onError('Diagnose konnte nicht entfernt werden.'),
  });
}

// ─── Merkmale ────────────────────────────────────────────────────────────

export function useTraits(personId: string) {
  return useQuery({
    queryKey: QUERY_KEYS.medical.traits(personId),
    queryFn: () => getTraits(personId),
    enabled: !!personId,
    // Siehe useConditions: Request-Welle der Vererbungs-Seite eindämmen.
    staleTime: 60_000,
  });
}

export function useCreateTrait(personId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dto: CreateTraitDto) => createTrait(personId, dto),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QUERY_KEYS.medical.traits(personId) });
      toast.success('Merkmal gespeichert.');
    },
    onError: onError('Merkmal konnte nicht gespeichert werden.'),
  });
}

export function useUpdateTrait(personId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, dto }: { id: string; dto: UpdateTraitDto }) => updateTrait(id, dto),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QUERY_KEYS.medical.traits(personId) });
      toast.success('Merkmal aktualisiert.');
    },
    onError: onError('Merkmal konnte nicht aktualisiert werden.'),
  });
}

export function useDeleteTrait(personId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteTrait(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QUERY_KEYS.medical.traits(personId) });
      toast.success('Merkmal entfernt.');
    },
    onError: onError('Merkmal konnte nicht entfernt werden.'),
  });
}

// ─── Einwilligung ─────────────────────────────────────────────────────────

export function useConsents(personId: string) {
  return useQuery({
    queryKey: QUERY_KEYS.medical.consents(personId),
    queryFn: () => getConsents(personId),
    enabled: !!personId,
  });
}

export function useGrantConsent(personId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dto: { scope: MedicalConsent['scope']; note?: string | null }) => grantConsent(personId, dto),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QUERY_KEYS.medical.consents(personId) });
      toast.success('Einwilligung dokumentiert.');
    },
    onError: onError('Einwilligung konnte nicht gespeichert werden.'),
  });
}

export function useRevokeConsent(personId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => revokeConsent(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QUERY_KEYS.medical.consents(personId) });
      toast.success('Einwilligung widerrufen.');
    },
    onError: onError('Einwilligung konnte nicht widerrufen werden.'),
  });
}
